import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/UserManagement.css";
import Header from "../components/Header.js";
import IDCardPopup from "../components/IDCardPopup.js";
import { getToken } from "../storage/Storage.js";
import { fetchUsers, userApproval, userUdidApproval } from "../services/AllServices.js";
import AdminSidebar from "../components/AdminSidebar.js";

const UserManagement = () => {
  const [activeItem, setActiveItem] = useState("userManagement");
  const [usersData, setUsersData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [openIDCardPopup, setOpenIDCardPopup] = useState(false);
  const [idCardUrl, setIdCardUrl] = useState("");
  const [token, setTokenState] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsersData = async () => {
      try {
        const response = await fetchUsers(token);
        console.log("API Response:", response);
        if (response?.status === "success") {
          setUsersData(response.data.reverse());
          console.log('Users data:', response.data);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    if (token) {
      fetchUsersData();
    }
  }, [token]);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const storedToken = await getToken();
        setTokenState(storedToken);
      } catch (error) {
        console.error("Failed to fetch token", error);
      }
    };
    fetchToken();
  }, []);

  const handleUserApproval = async (userId, status) => {
    console.log("User ID:", userId, "Status:", status);
    try {
      const user = usersData.find(u => u._id === userId);
      
      await userApproval({ 
        UserID: userId, 
        status: status,
        udid: user?.udid || false
      }, (response) => {
        if (response?.status === "success") {
          setUsersData((prevUsers) =>
            prevUsers.map((user) =>
              user._id === userId ? { ...user, status } : user
            )
          );
        }
      });
    } catch (error) {
      console.error(`Error updating user status to ${status}:`, error);
    }
  };

  const handleUdidApproval = async (userId, action) => {
    console.log("User ID:", userId, "UDID Action:", action);
    try {
      let newUdidStatus;
      
      if (action === "approve" || action === "enable") {
        newUdidStatus = true;
      } else if (action === "reject" || action === "disable") {
        newUdidStatus = false;
      }

      await userUdidApproval({ 
        UserID: userId, 
        udid: newUdidStatus
      }, (response) => {
        if (response?.status === "success") {
          setUsersData((prevUsers) =>
            prevUsers.map((user) =>
              user._id === userId ? { ...user, udid: newUdidStatus } : user
            )
          );
        }
      });
    } catch (error) {
      console.error(`Error updating UDID status:`, error);
    }
  };

  const resetSearch = () => {
    setSearchQuery("");
  };

  const handleActiveItemChange = (item) => {
    setActiveItem(item);
    if (item === "bookManagement") {
      navigate("/Book-Management");
    } else if (item === "userManagement") {
      navigate("/User-Management");
    }
    resetSearch();
  };

  const handleIDCardClick = (idCardUrl) => {
    if (idCardUrl) {
      setIdCardUrl(idCardUrl);
      setOpenIDCardPopup(true);
    }
  };

  const handleProfileClick = () => {
    navigate("/settings", { state: { selectedSection: "account" } });
  };

  const getHeaderVisibility = () => {
    if (activeItem === "bookManagement" || activeItem === "") {
      return { showSearch: false, showUserProfile: true, showArrows: false, pageName: "Book Management" };
    } else if (activeItem === "userManagement") {
      return { showSearch: false, showUserProfile: true, showArrows: false, pageName: "User Management" };
    } 
  };

  const { showSearch, showUserProfile, showArrows, pageName } = getHeaderVisibility();

  const handleImageError = (e) => {
    e.target.style.display = 'none';
    const parent = e.target.parentElement;
    const noUdidText = document.createElement('div');
    noUdidText.className = 'no-udid-text';
    noUdidText.textContent = 'No UDID';
    parent.appendChild(noUdidText);
  };

  // Helper function to determine UDID status display
  const getUdidStatus = (user) => {
    const hasUdidUpload = !!user.profile_picture_url;
    
    if (!hasUdidUpload) {
      return { status: "rejected", label: "Invalid" };
    }
    
    if (user.udid === true) {
      return { status: "approved", label: "Valid" };
    } else if (user.udid === false) {
      return { status: "rejected", label: "Invalid" };
    } else {
      return { status: "pending", label: "Pending" };
    }
  };

  // Helper function to determine UDID approval buttons - FIXED VERSION
  const getUdidApprovalButtons = (user) => {
    const hasUdidUpload = !!user.profile_picture_url;
    
    if (!hasUdidUpload) {
      return <span className="no-action-text">No UDID</span>;
    }
    
    // For newly signed up users (udid is null/undefined) - show approve/reject buttons
    if (user.udid === undefined || user.udid === null) {
      return (
        <div className="udid-buttons-container">
          <button
            className="udid-approve-btn"
            onClick={() => handleUdidApproval(user._id, "approve")}
          >
            <span style={{ color: "#2d8a57" }}>✔</span>
            <span>Approve</span>
          </button>
          <button
            className="udid-reject-btn"
            onClick={() => handleUdidApproval(user._id, "reject")}
          >
            <span style={{ color: "#d9534f" }}>✖</span>
            <span>Reject</span>
          </button>
        </div>
      );
    }
    
    // For already processed users - show only the opposite action button
    if (user.udid === true) {
      // If UDID is currently enabled, show only Disable button
      return (
        <div className="udid-buttons-container">
          <button
            className="udid-reject-btn"
            onClick={() => handleUdidApproval(user._id, "disable")}
          >
            <span style={{ color: "#d9534f" }}>✖</span>
            <span>Disable</span>
          </button>
        </div>
      );
    } else {
      // If UDID is currently disabled, show only Enable button
      return (
        <div className="udid-buttons-container">
          <button
            className="udid-approve-btn"
            onClick={() => handleUdidApproval(user._id, "enable")}
          >
            <span style={{ color: "#2d8a57" }}>✔</span>
            <span>Enable</span>
          </button>
        </div>
      );
    }
  };

  return (
    <main className="main-content">
      <div className="sidebar_container">
        <AdminSidebar
          activeItem={activeItem}
          setActiveItem={handleActiveItemChange}
          resetSearch={resetSearch}
        />
      </div>

      <div className="dashboard_container">
        <div className="header_container">
          <Header
            showSearch={showSearch}
            showUserProfile={showUserProfile}
            showArrows={showArrows}
            pageName={pageName}
            searchQuery={searchQuery}
            onSearch={(query) => setSearchQuery(query)}
            onProfileClick={handleProfileClick}
          />
        </div>
        <div className="table_container">
          <table className="users_table">
            <thead>
              <tr>
                <th>User</th>
                <th>Name</th>
                <th>Email Address</th>
                <th>UDID Status</th>
                <th>UDID Approval</th>
                <th>User Approval</th>
              </tr>
            </thead>
            <tbody>
              {usersData.map((user) => {
                const udidInfo = getUdidStatus(user);
                const udidButtons = getUdidApprovalButtons(user);
                
                return (
                  <tr key={user._id}>
                    <td>
                      <div className="avatarr-container">
                        {user.profile_picture_url ? (
                          <img
                            src={user.profile_picture_url}
                            alt={user.fullname}
                            className="avatarr"
                            onClick={() => handleIDCardClick(user.profile_picture_url)}
                            onError={handleImageError}
                          />
                        ) : (
                          <div className="no-udid-text">NA</div>
                        )}
                      </div>
                    </td>
                    <td>{user.fullname}</td>
                    <td>{user.email}</td>
                    <td>
                      <div className={`udid-status ${udidInfo.status}`}>
                        <span>{udidInfo.label}</span>
                      </div>
                    </td>
                    <td>
                      {udidButtons}
                    </td>
                    <td>
                      {user.status === "pending" ? (
                        <div className="approve_reject_container">
                          <button
                            className="approve_btn"
                            onClick={() => handleUserApproval(user._id, "approved")}
                          >
                            <span style={{ color: "#2d8a57" }}>✔</span>
                            <span>Approve</span>
                          </button>
                          <button
                            className="reject_btn"
                            onClick={() => handleUserApproval(user._id, "rejected")}
                          >
                            <span style={{ color: "#d9534f" }}>✖</span>
                            <span>Reject</span>
                          </button>
                        </div>
                      ) : (
                        <button className={`status_btn ${user.status}`}>{user.status}</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <IDCardPopup
            open={openIDCardPopup}
            idCardUrl={idCardUrl}
            onClose={() => setOpenIDCardPopup(false)}
          />
        </div>
      </div>
    </main>
  );
};

export default UserManagement;